import React, { useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Toolbar,
    Button,
    Snackbar,
    Alert,
    TextField
} from '@mui/material';
import {
    Save,
    Settings,
    GridOn,
    FileCopy,
    Refresh
} from '@mui/icons-material';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { WelcomeBoardLayout, WelcomeBoardItem } from '../../../common/interfaces';
import { LayoutEditorProps } from './types';
import { DRAWER_WIDTH, DROPDOWN_HEIGHT, DROPDOWN_MARGIN, DROPDOWN_PADDING } from './constants';
import { parseGridTemplate, parseGridPosition, wouldOverlap } from './utils';
import { useLayoutState } from './hooks/useLayoutState';
import { useDragAndDrop } from './hooks/useDragAndDrop';
import { WidgetPalette } from './components/WidgetPalette';
import { SpanningGridWidget } from './components/SpanningGridWidget';
import { GridCell } from './components/GridCell';
import { WidgetConfigDialog } from './dialogs/WidgetConfigDialog';
import { GridConfigDialog } from './dialogs/GridConfigDialog';
import { ExampleLayoutsDialog } from './dialogs/ExampleLayoutsDialog';

export const LayoutEditor: React.FC<LayoutEditorProps> = ({
    initialLayout,
    onSave
}) => {
    const {
        layout,
        setLayout,
        selectedWidget,
        setSelectedWidget,
        originalSelectedWidget,
        setOriginalSelectedWidget,
        widgetConfig,
        setWidgetConfig,
        drawerOpen,
        setDrawerOpen,
        configDialogOpen,
        setConfigDialogOpen,
        gridConfigOpen,
        setGridConfigOpen,
        exampleLayoutsOpen,
        setExampleLayoutsOpen,
        showAlert,
        setShowAlert,
        alertMessage,
        setAlertMessage,
        handleWidgetClick,
        handleWidgetDelete,
        showAlertMessage,
        handleClearLayout,
        closeConfigDialog
    } = useLayoutState(initialLayout);

    const { handleWidgetDrop } = useDragAndDrop(layout, setLayout, showAlertMessage);

    const gridColumns = parseGridTemplate(layout.gridColumnsTemplate);
    const gridRows = parseGridTemplate(layout.gridRowsTemplate);

    // Widget update handler with validation
    const handleWidgetUpdate = useCallback((updatedItem: WelcomeBoardItem) => {
        if (!originalSelectedWidget) return;

        const otherWidgets = layout.welcomeBoardItems.filter(w => w !== originalSelectedWidget);
        const hasConflict = wouldOverlap(
            widgetConfig.colStart,
            widgetConfig.colSpan,
            widgetConfig.rowStart,
            widgetConfig.rowSpan,
            otherWidgets
        );

        if (hasConflict) {
            showAlertMessage('The specified position conflicts with existing widgets');
            return;
        }

        setLayout(prev => ({
            ...prev,
            welcomeBoardItems: prev.welcomeBoardItems.map(item =>
                item === originalSelectedWidget ? updatedItem : item
            )
        }));
        closeConfigDialog();
    }, [originalSelectedWidget, layout.welcomeBoardItems, widgetConfig, setLayout, showAlertMessage, closeConfigDialog]);

    // Grid configuration handlers
    const handleGridConfigSave = useCallback((newLayout: WelcomeBoardLayout) => {
        setLayout(newLayout);
        setGridConfigOpen(false);
    }, [setLayout, setGridConfigOpen]);

    // Example layout handlers
    const handleLoadExampleLayout = useCallback((exampleLayout: WelcomeBoardLayout) => {
        const layoutToLoad = {
            ...exampleLayout,
            id: layout.id,
            clientId: layout.clientId,
            name: layout.name
        };
        setLayout(layoutToLoad);
        setExampleLayoutsOpen(false);
        showAlertMessage(`Example layout "${exampleLayout.name}" loaded successfully!`);
    }, [layout.id, layout.clientId, layout.name, setLayout, setExampleLayoutsOpen, showAlertMessage]);

    const handleSave = useCallback(() => {
        onSave(layout);
    }, [layout, onSave]);

    const handleNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setLayout(prev => ({
            ...prev,
            name: event.target.value
        }));
    }, [setLayout]);

    // Render grid cells with spanning widget support
    const renderGridCells = () => {
        const cells = [];
        const processedWidgets = new Set<WelcomeBoardItem>();

        for (let row = 1; row <= gridRows; row++) {
            for (let col = 1; col <= gridColumns; col++) {
                // Check if this cell is occupied by any widget
                const occupyingWidget = layout.welcomeBoardItems.find(widget => {
                    const colPos = parseGridPosition(widget.gridColumn);
                    const rowPos = parseGridPosition(widget.gridRow);

                    return col >= colPos.start && col < (colPos.start + colPos.span) &&
                        row >= rowPos.start && row < (rowPos.start + rowPos.span);
                });

                if (occupyingWidget && !processedWidgets.has(occupyingWidget)) {
                    // This is the starting cell of a spanning widget
                    const colPos = parseGridPosition(occupyingWidget.gridColumn);
                    const rowPos = parseGridPosition(occupyingWidget.gridRow);

                    processedWidgets.add(occupyingWidget);

                    cells.push(
                        <SpanningGridWidget
                            key={`${col}-${row}-widget`}
                            item={occupyingWidget}
                            colStart={colPos.start}
                            colSpan={colPos.span}
                            rowStart={rowPos.start}
                            rowSpan={rowPos.span}
                            onWidgetClick={handleWidgetClick}
                            onWidgetDelete={handleWidgetDelete}
                        />
                    );
                } else if (!occupyingWidget) {
                    // Empty cell - render drop zone
                    cells.push(
                        <GridCell
                            key={`${col}-${row}`}
                            column={col}
                            row={row}
                            onWidgetDrop={handleWidgetDrop}
                            onWidgetDelete={handleWidgetDelete}
                        />
                    );
                }
                // If occupied by already processed widget, skip (no cell needed)
            }
        }

        return cells;
    };

    return (
        <DndProvider backend={HTML5Backend}>
            <Box sx={{ display: 'flex', height: '100%' }}>
                {/* Widget Palette */}
                <WidgetPalette open={drawerOpen} />

                {/* Main Editor Area */}
                <Box sx={{
                    width: `calc(100% - ${DRAWER_WIDTH}px)`,
                    height: `100%`
                }}>
                    {/* Toolbar */}
                    <Toolbar sx={{justifyContent: 'space-between'}}>
                        <Box sx={{display: 'flex', width: '50%'}}>
                            <TextField
                                value={layout.name}
                                onChange={handleNameChange}
                                variant="standard"
                                InputProps={{
                                    sx: {
                                        fontSize: '1.25rem',
                                        fontWeight: 500
                                    }
                                }}
                                sx={{ flexGrow: 1, mr: 2 }}
                                placeholder="Layout name"
                            />
                            {/* <Typography variant="h6" sx={{ mr: 2 }}>
                                ({gridColumns}×{gridRows})
                            </Typography> */}
                        </Box>
                        <Box>
                            <Button
                                startIcon={<GridOn />}
                                onClick={() => setGridConfigOpen(true)}
                                sx={{ mr: 1 }}
                            >
                                Grid
                            </Button>
                            {/* <Button
                                startIcon={<FileCopy />}
                                onClick={() => setExampleLayoutsOpen(true)}
                                sx={{ mr: 1 }}
                            >
                                Examples
                            </Button> */}
                            <Button
                                startIcon={<Refresh />}
                                onClick={handleClearLayout}
                                sx={{ mr: 1 }}
                            >
                                Clear
                            </Button>
                            <Button
                                startIcon={<Settings />}
                                onClick={() => setDrawerOpen(!drawerOpen)}
                                sx={{ mr: 1 }}
                            >
                                Palette
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<Save />}
                                onClick={handleSave}
                            >
                                Save Layout
                            </Button>
                        </Box>
                    </Toolbar>

                    {/* Grid Editor */}
                    <Box sx={{ flexGrow: 1, p: 2, overflow: 'hidden', height: `calc(100% - ${DROPDOWN_HEIGHT + DROPDOWN_MARGIN + DROPDOWN_PADDING + 10}px)` }}>
                        <Paper
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: layout.gridColumnsTemplate,
                                gridTemplateRows: layout.gridRowsTemplate,
                                gap: 1,
                                minHeight: '600px',
                                backgroundColor: 'grey.50',
                                height: '100%'
                            }}
                        >
                            {renderGridCells()}
                        </Paper>
                    </Box>
                </Box>

                {/* Widget Configuration Dialog */}
                <WidgetConfigDialog
                    open={configDialogOpen}
                    selectedWidget={selectedWidget}
                    originalSelectedWidget={originalSelectedWidget}
                    widgetConfig={widgetConfig}
                    layout={layout}
                    gridColumns={gridColumns}
                    gridRows={gridRows}
                    onClose={closeConfigDialog}
                    onWidgetChange={setSelectedWidget}
                    onConfigChange={setWidgetConfig}
                    onUpdate={handleWidgetUpdate}
                />

                {/* Grid Configuration Dialog */}
                <GridConfigDialog
                    open={gridConfigOpen}
                    layout={layout}
                    onClose={() => setGridConfigOpen(false)}
                    onSave={handleGridConfigSave}
                    onAlert={showAlertMessage}
                />

                {/* Example Layouts Dialog */}
                <ExampleLayoutsDialog
                    open={exampleLayoutsOpen}
                    onClose={() => setExampleLayoutsOpen(false)}
                    onLoadLayout={handleLoadExampleLayout}
                />

                {/* Alert Snackbar */}
                <Snackbar
                    open={showAlert}
                    autoHideDuration={6000}
                    onClose={() => setShowAlert(false)}
                >
                    <Alert onClose={() => setShowAlert(false)} severity="info">
                        {alertMessage}
                    </Alert>
                </Snackbar>
            </Box>
        </DndProvider>
    );
};